import { API_ROUTES } from '@dgm/shared';

/**
 * Thin fetch client for the HTTP API. Auth is cookie-based (HttpOnly), so we
 * always send `credentials: 'include'` and never touch tokens in JS
 * (decisions-log #15). On a 401 we attempt a single silent refresh, then retry.
 */
const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  isRetry = false,
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    credentials: 'include',
    // X-Requested-With forces a CORS preflight on every call — our CSRF guard
    // for the SameSite=None auth cookies (a cross-site attacker's origin fails
    // the preflight against the API's strict origin allowlist).
    headers: {
      'x-requested-with': 'fetch',
      ...(body ? { 'content-type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 401 && !isRetry && path !== API_ROUTES.refresh.path) {
    const refreshed = await tryRefresh();
    if (refreshed) return request<T>(method, path, body, true);
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) {
    throw new ApiError(res.status, data.error ?? `Request failed (${res.status})`, data.details);
  }
  return data as T;
}

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_URL}${API_ROUTES.refresh.path}`, {
      method: 'POST',
      credentials: 'include',
    });
    return res.ok;
  } catch {
    return false;
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
};

export { API_URL };
